/**
 * React Query hooks for chat module settings
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  chatSettingsApi,
  type ChatUserSettingsUpdate,
  type ChatTenantSettingsUpdate,
  type ChatSysadminSettingsUpdate,
} from '../services/chatSettingsApi';

const USER_SETTINGS_QUERY_KEY = ['chat-user-settings'];
const MODEL_CATEGORIES_QUERY_KEY = ['chat-model-categories'];
const TENANT_SETTINGS_QUERY_KEY = ['chat-tenant-settings'];
const SYSADMIN_SETTINGS_QUERY_KEY = ['chat-sysadmin-settings'];

// ========== User Settings ==========

export function useChatUserSettings() {
  return useQuery({
    queryKey: USER_SETTINGS_QUERY_KEY,
    queryFn: chatSettingsApi.getUserSettings,
  });
}

export function useUpdateChatUserSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ChatUserSettingsUpdate) => chatSettingsApi.updateUserSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_SETTINGS_QUERY_KEY });
    },
  });
}

export function useModelCategories() {
  return useQuery({
    queryKey: MODEL_CATEGORIES_QUERY_KEY,
    queryFn: chatSettingsApi.getModelCategories,
  });
}

// ========== Tenant Settings (Admin) ==========

export function useChatTenantSettings() {
  return useQuery({
    queryKey: TENANT_SETTINGS_QUERY_KEY,
    queryFn: chatSettingsApi.getTenantSettings,
  });
}

export function useUpdateChatTenantSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ChatTenantSettingsUpdate) => chatSettingsApi.updateTenantSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TENANT_SETTINGS_QUERY_KEY });
      // allow_user_model_choice flows into the user-settings response, which gates
      // the chat model picker. Invalidate it so the change is visible immediately.
      queryClient.invalidateQueries({ queryKey: USER_SETTINGS_QUERY_KEY });
    },
  });
}

// ========== Sysadmin Settings ==========

export function useChatSysadminSettings() {
  return useQuery({
    queryKey: SYSADMIN_SETTINGS_QUERY_KEY,
    queryFn: chatSettingsApi.getSysadminSettings,
  });
}

export function useUpdateChatSysadminSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ChatSysadminSettingsUpdate) => chatSettingsApi.updateSysadminSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SYSADMIN_SETTINGS_QUERY_KEY });
    },
  });
}
